'use client';

import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import {
  type VerificationBannerState,
  VerificationBanner as VerificationBannerUI
} from '@ui/components/features/verification';
import { Button } from '@ui/components/ui/button';
import { useCurrentProfessional } from '@website/hooks/useCurrentProfessional';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { isProfessionalBioComplete } from '@website/utils/profileUtils';

/**
 * Sitewide nudge surfaced under the header for mentors whose account is not
 * yet approved.
 *
 * Visibility rules:
 *
 * - Only shown when the user is authenticated AND `user.isProfessional`.
 * - Only shown when `professional.status !== 'approved'`. Once the main
 *   moderation flag flips to `approved` it stays sticky through the
 *   `professionalRevisions` flow, so edit-related "pending" states never
 *   trigger this banner.
 * - Hidden on the onboarding wizard, the activity creation wizard, and
 *   anywhere inside the user menu (`/settings/*`) since those already
 *   surface the dedicated "Complete your profile" entry.
 *
 * Copy focuses on profile completion (photo + ID documents + any other
 * missing items) rather than on ID verification specifically — the nudge
 * always points the user to the single `/settings/complete-profile` page,
 * where they can see exactly what's missing.
 */
export function VerificationBanner() {
  const locale = useLocale();
  const t = useTranslations('VerificationBanner');
  const pathname = usePathname();
  const { data: user } = useCurrentUser();
  const { professional } = useCurrentProfessional();

  if (!user?.isProfessional) return null;
  if (!professional) return null;
  if (professional.status === 'approved') return null;

  // Avoid repeating the banner on pages that already surface the same CTA:
  // the onboarding wizard plus every user-menu page (`/settings/*`,
  // including the complete-profile checklist itself).
  if (pathname?.includes('/onboarding')) return null;
  if (isActivityCreatePath(pathname, locale)) return null;
  if (isUserMenuPath(pathname, locale)) return null;

  const hasPhoto = !!user.profilePictureKey;
  const hasDocument = !!user.verificationDocumentKey;
  const hasBio = isProfessionalBioComplete(user);
  const hasMissingItems = !hasPhoto || !hasDocument || !hasBio;

  // Pull the decline reason straight off the professional — the backend
  // preserves it across the incomplete → pending transition so we can keep
  // showing the "why" until the mentor is approved. The post-approval edit
  // flow (`latestRevision`) still uses its own reason field.
  const latestDecline = professional.latestRevision?.status === 'declined' ? professional.latestRevision : null;
  const rejectionReason = professional.rejectionReason ?? latestDecline?.rejectionReason;
  const wasPreviouslyDeclined = !!professional.rejectionReason;

  // Map the 4-state moderation enum to the 3 banner variants:
  //   - incomplete + prior decline → rejected (show reason, red CTA)
  //   - incomplete (no prior decline) → submit (green "complete" CTA)
  //   - pending → under-review (informational, no CTA)
  //   - legacy `rejected` → rejected (kept for back-compat)
  const state: VerificationBannerState =
    professional.status === 'rejected'
      ? 'rejected'
      : professional.status === 'incomplete'
        ? wasPreviouslyDeclined
          ? 'rejected'
          : 'submit'
        : hasMissingItems
          ? 'submit'
          : 'under-review';

  const translations = {
    submitTitle: t('submitTitle'),
    submitDescription: t('submitDescription'),
    submitCta: t('submitCta'),
    underReviewTitle: t('underReviewTitle'),
    underReviewDescription: t('underReviewDescription'),
    rejectedTitle: t('rejectedTitle'),
    rejectedDescription: t('rejectedDescription'),
    rejectedCta: t('rejectedCta')
  };

  const completeProfileHref = `/${locale}/settings/complete-profile`;

  const ctaSlot =
    state === 'submit' ? (
      <Button asChild size="sm">
        <Link href={completeProfileHref}>{translations.submitCta}</Link>
      </Button>
    ) : state === 'rejected' ? (
      <Button asChild size="sm" variant="destructive">
        <Link href={completeProfileHref}>{translations.rejectedCta}</Link>
      </Button>
    ) : undefined;

  return (
    <VerificationBannerUI
      state={state}
      translations={translations}
      ctaSlot={ctaSlot}
      rejectionReason={rejectionReason}
    />
  );
}

/**
 * Returns true when the current pathname lives inside the user menu
 * (`/<locale>/settings` or any of its subpages).
 */
function isUserMenuPath(pathname: string | null, locale: string): boolean {
  if (!pathname) return false;
  const prefix = `/${locale}`;
  const normalized = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : pathname;
  return normalized === '/settings' || normalized.startsWith('/settings/');
}

/**
 * Returns true when the current pathname is the activity creation wizard
 * (`/<locale>/activity/create` or any nested step). The wizard already has
 * its own profile-completion guidance, so the sitewide nudge would be noisy.
 */
function isActivityCreatePath(pathname: string | null, locale: string): boolean {
  if (!pathname) return false;
  const prefix = `/${locale}`;
  const normalized = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : pathname;
  return normalized === '/activity/create' || normalized.startsWith('/activity/create/');
}
